import type {
  VolumeCompareBarGroup,
  VolumeCompareSnapshot,
  VolumeCompareWindow,
} from "./volumeCompareChart.types";
import moment from "moment-timezone";

export const VOLUME_COMPARE_WINDOWS: VolumeCompareWindow[] = [
  {
    period: 10,
    label: "10 Day",
    shortLabel: "10d",
    avgKey: "10day_av",
    vsKey: "v_vs_10day_av",
  },
  {
    period: 20,
    label: "20 Day",
    shortLabel: "20d",
    avgKey: "20day_av",
    vsKey: "v_vs_20day_av",
  },
  {
    period: 50,
    label: "50 Day",
    shortLabel: "50d",
    avgKey: "50day_av",
    vsKey: "v_vs_50day_av",
  },
  {
    period: 100,
    label: "100 Day",
    shortLabel: "100d",
    avgKey: "100day_av",
    vsKey: "v_vs_100day_av",
  },
  {
    period: 200,
    label: "200 Day",
    shortLabel: "200d",
    avgKey: "200day_av",
    vsKey: "v_vs_200day_av",
  },
];

export function formatVolumeAxis(value: number): string {
  const n = Math.abs(value);
  if (n >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(Math.round(value));
}

export function formatVolumeMultiple(multiple: number): string {
  if (!Number.isFinite(multiple) || multiple <= 0) return "—";
  if (multiple < 1) {
    const compact = multiple.toFixed(2);
    return `${compact.startsWith("0.") ? compact.slice(1) : compact}x`;
  }
  if (multiple < 1.05) return `${multiple.toFixed(2)}x`;
  if (multiple >= 10) return `${Math.round(multiple)}x`;
  return `${multiple.toFixed(1)}x`;
}

export function formatVolumeCompareDate(date: string | undefined): string {
  if (!date) return "";

  const parsed = moment.tz(
    date,
    ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601],
    "Asia/Kolkata",
  );
  if (parsed.isValid()) {
    return parsed.format("DD-MM-YYYY");
  }

  return date;
}

function parseNumber(value: number | string | null | undefined): number {
  if (value == null || value === undefined) return 0;
  const n =
    typeof value === "string"
      ? parseFloat(value.replace(/,/g, ""))
      : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function resolveVolumeMultiple(
  _snapshot: VolumeCompareSnapshot,
  _window: VolumeCompareWindow,
  avgVolume: number,
  todayVolume: number,
): number {
  if (!avgVolume || avgVolume <= 0) return 0;
  return todayVolume / avgVolume;
}

/** Build grouped bar rows for 10 / 20 / 50 / 100 / 200 day windows. */
export function buildVolumeCompareBarGroups(
  snapshot: VolumeCompareSnapshot | null | undefined,
): VolumeCompareBarGroup[] {
  if (!snapshot) return [];

  const todayVolume = parseNumber(snapshot.volume);

  return VOLUME_COMPARE_WINDOWS.map((window) => {
    const avgVolume = parseNumber(snapshot[window.avgKey]);
    return {
      period: window.period,
      label: window.label,
      shortLabel: window.shortLabel,
      avgVolume,
      todayVolume,
      volumeMultiple: resolveVolumeMultiple(
        snapshot,
        window,
        avgVolume,
        todayVolume,
      ),
    };
  }).filter((g) => g.avgVolume > 0);
}

export function hasVolumeCompareData(
  snapshot: VolumeCompareSnapshot | null | undefined,
): boolean {
  return buildVolumeCompareBarGroups(snapshot).length > 0;
}

/** Muted gray for average volume bars. */
export function getAvgBarColor(_darkMode: boolean): string {
  return "#5ba78d";
}

/** White for the selected day's volume bar. */
export function getTodayBaseBarColor(_darkMode: boolean): string {
  return "#63a3b3";
}

export function isVolumeCompareDateToday(date: string | undefined): boolean {
  if (!date?.trim()) return false;

  const todayKey = moment.tz("Asia/Kolkata").format("DD-MM-YYYY");
  const snapshotKey = formatVolumeCompareDate(date);

  return snapshotKey === todayKey;
}

export function getSelectedVolumeLabel(
  snapshot: VolumeCompareSnapshot,
): string {
  if (isVolumeCompareDateToday(snapshot.date)) {
    return "Volume as on Today";
  }

  const dateLabel = formatVolumeCompareDate(snapshot.date);
  return dateLabel ? `Volume as on ${dateLabel}` : "Selected volume";
}

export function buildVolumeCompareTooltipHtml(
  group: VolumeCompareBarGroup,
  snapshot: VolumeCompareSnapshot,
  darkMode: boolean,
  selectedVolumeLabel?: string,
): string {
  const text = darkMode ? "#e2e8f0" : "#1e293b";
  const muted = darkMode ? "#94a3b8" : "#64748b";
  const dateLabel = formatVolumeCompareDate(snapshot.date);
  const volumeLabel = selectedVolumeLabel ?? getSelectedVolumeLabel(snapshot);

  return `
    <div style="font-size:12px;line-height:1.55;">
      <div style="font-weight:600;color:${text};margin-bottom:6px;">${group.label} · ${dateLabel}</div>
      <div style="display:flex;justify-content:space-between;gap:14px;">
        <span style="color:${muted}">Avg volume</span>
        <span style="color:${text};font-weight:600">${formatVolumeAxis(group.avgVolume)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;gap:14px;">
        <span style="color:${muted}">${volumeLabel}</span>
        <span style="color:${text};font-weight:600">${formatVolumeAxis(group.todayVolume)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;gap:14px;margin-top:4px;">
        <span style="color:${muted}">Times greater</span>
        <span style="color:${text};font-weight:700">${formatVolumeMultiple(group.volumeMultiple)}</span>
      </div>
    </div>
  `;
}

/** Normalize API row (supports snake_case keys from DB export). */
export function normalizeVolumeCompareSnapshot(
  row: Record<string, unknown>,
): VolumeCompareSnapshot {
  return {
    date: formatVolumeCompareDate(String(row.date ?? "")),
    symbol: row.symbol != null ? String(row.symbol) : undefined,
    volume: parseNumber(row.volume as number | null),
    "10day_av": (row["10day_av"] ?? row["10_days_avg_volume"]) as number | null,
    "20day_av": (row["20day_av"] ?? row["20_days_avg_volume"]) as number | null,
    "50day_av": (row["50day_av"] ?? row["50_days_avg_volume"]) as number | null,
    "100day_av": (row["100day_av"] ?? row["100_days_avg_volume"]) as
      | number
      | null,
    "200day_av": (row["200day_av"] ?? row["200_days_avg_volume"]) as
      | number
      | null,
    v_vs_10day_av: (row.v_vs_10day_av ?? row.v_vs_10_day_av) as number | null,
    v_vs_20day_av: (row.v_vs_20day_av ?? row.v_vs_20_day_av) as number | null,
    v_vs_50day_av: (row.v_vs_50day_av ?? row.v_vs_50_day_av) as number | null,
    v_vs_100day_av: (row.v_vs_100day_av ?? row.v_vs_100_day_av) as
      | number
      | null,
    v_vs_200day_av: (row.v_vs_200day_av ?? row.v_vs_200_day_av) as
      | number
      | null,
  };
}

export function recalculateVolumeSnapshotWithNewVolume(
  snapshot: VolumeCompareSnapshot,
  newVolume: number,
): VolumeCompareSnapshot {
  if (snapshot.volume === newVolume) return snapshot;

  const oldVolume = snapshot.volume;
  const volumeDiff = newVolume - oldVolume;

  const getNewAvg = (oldAvg: number | string | null, period: number) => {
    if (oldAvg == null) return null;
    const oldAvgNum = Number(oldAvg);
    if (isNaN(oldAvgNum)) return null;
    const newAvg = oldAvgNum + volumeDiff / period;
    return newAvg > 0 ? Number(newAvg.toFixed(2)) : 0;
  };

  const new10Day = getNewAvg(snapshot["10day_av"], 10);
  const new20Day = getNewAvg(snapshot["20day_av"], 20);
  const new50Day = getNewAvg(snapshot["50day_av"], 50);
  const new100Day = getNewAvg(snapshot["100day_av"], 100);
  const new200Day = getNewAvg(snapshot["200day_av"], 200);

  return {
    ...snapshot,
    volume: newVolume,
    "10day_av": new10Day,
    "20day_av": new20Day,
    "50day_av": new50Day,
    "100day_av": new100Day,
    "200day_av": new200Day,
    v_vs_10day_av: new10Day
      ? Number((newVolume / new10Day).toFixed(2))
      : snapshot.v_vs_10day_av,
    v_vs_20day_av: new20Day
      ? Number((newVolume / new20Day).toFixed(2))
      : snapshot.v_vs_20day_av,
    v_vs_50day_av: new50Day
      ? Number((newVolume / new50Day).toFixed(2))
      : snapshot.v_vs_50day_av,
    v_vs_100day_av: new100Day
      ? Number((newVolume / new100Day).toFixed(2))
      : snapshot.v_vs_100day_av,
    v_vs_200day_av: new200Day
      ? Number((newVolume / new200Day).toFixed(2))
      : snapshot.v_vs_200day_av,
  };
}
